import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Plus, Pencil, Trash2, Send, X, UserPlus, Check } from "lucide-react";
import ProfilePhoto from "@/components/ProfilePhoto";
import { categoryLabel } from "@/lib/constants";

/**
 * Client-only "Équipes habituelles" section (spec §12).
 * Group favourites into named teams and invite them in one click on a mission.
 */
export default function ClientTeams({ openMissions = [] }) {
    const [teams, setTeams] = useState([]);
    const [favs, setFavs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // team or {} for create
    const [inviting, setInviting] = useState(null); // team to invite

    const refresh = async () => {
        setLoading(true);
        try {
            const [tRes, fRes] = await Promise.all([
                api.get("/client/teams"),
                api.get("/client/favorites"),
            ]);
            setTeams(tRes.data || []);
            setFavs(fRes.data || []);
        } catch (_) {
            setTeams([]); setFavs([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { refresh(); }, []);

    const removeTeam = async (id) => {
        if (!window.confirm("Supprimer cette équipe ? Les favoris ne sont pas supprimés.")) return;
        try {
            await api.delete(`/client/teams/${id}`);
            toast.success("Équipe supprimée");
            refresh();
        } catch (_) {
            toast.error("Erreur");
        }
    };

    return (
        <section className="sd-card p-6 mb-8" data-testid="client-teams-section">
            <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                <div>
                    <div className="overline flex items-center gap-2">
                        <Users size={12} className="text-[#F5A623]" />
                        Équipes habituelles
                    </div>
                    <h2 className="font-display text-xl">
                        Vos équipes {teams.length > 0 && <span className="text-sm text-slate-400 font-normal">({teams.length})</span>}
                    </h2>
                    <p className="text-xs text-slate-500 mt-1 max-w-lg">
                        Regroupez vos favoris sous un nom (« Cocktail Lyon », « Serveurs weekend ») et
                        invitez-les en priorité sur une mission ouverte, avant la diffusion publique.
                    </p>
                </div>
                <button
                    onClick={() => setEditing({ name: "", member_user_ids: [], note: "" })}
                    className="btn-gold text-xs inline-flex items-center gap-1"
                    data-testid="team-create-btn"
                    disabled={favs.length === 0}
                    title={favs.length === 0 ? "Ajoutez d'abord des favoris" : "Créer une équipe"}
                >
                    <Plus size={14} /> Nouvelle équipe
                </button>
            </div>

            {loading && <div className="text-sm text-slate-400 text-center py-6">Chargement…</div>}

            {!loading && teams.length === 0 && (
                <div className="text-sm text-slate-400 text-center py-8" data-testid="client-teams-empty">
                    {favs.length === 0
                        ? "Ajoutez d'abord des favoris pour pouvoir composer une équipe habituelle."
                        : "Aucune équipe pour l'instant. Créez-en une pour rappeler vos favoris en un clic."}
                </div>
            )}

            {!loading && teams.length > 0 && (
                <div className="grid md:grid-cols-2 gap-4">
                    {teams.map((t) => (
                        <div
                            key={t.id}
                            className="rounded-xl border border-slate-200 bg-white p-4"
                            data-testid={`team-card-${t.id}`}
                        >
                            <div className="flex items-start justify-between gap-2 mb-3">
                                <div className="min-w-0">
                                    <div className="font-semibold text-[#0B192C] truncate">{t.name}</div>
                                    <div className="text-[11px] text-slate-500">
                                        {t.members?.length || 0} membre{(t.members?.length || 0) > 1 ? "s" : ""}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button
                                        onClick={() => setEditing(t)}
                                        className="w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-[#0B192C] hover:bg-slate-100"
                                        title="Modifier"
                                        data-testid={`team-edit-${t.id}`}
                                    >
                                        <Pencil size={13} />
                                    </button>
                                    <button
                                        onClick={() => removeTeam(t.id)}
                                        className="w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50"
                                        title="Supprimer"
                                        data-testid={`team-delete-${t.id}`}
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            </div>

                            <div className="flex -space-x-2 mb-3">
                                {(t.members || []).slice(0, 6).map((m) => (
                                    <div
                                        key={m.user_id}
                                        className="w-8 h-8 rounded-full ring-2 ring-white overflow-hidden bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500"
                                        title={`${m.first_name} ${m.last_name} — ${categoryLabel(m.primary_job)}`}
                                    >
                                        {m.has_portrait ? (
                                            <ProfilePhoto candidateUserId={m.user_id} size={32} />
                                        ) : (
                                            (m.first_name || "?").charAt(0).toUpperCase()
                                        )}
                                    </div>
                                ))}
                                {(t.members || []).length > 6 && (
                                    <div className="w-8 h-8 rounded-full ring-2 ring-white bg-slate-100 text-[10px] font-bold text-slate-500 flex items-center justify-center">
                                        +{t.members.length - 6}
                                    </div>
                                )}
                                {(t.members || []).length === 0 && (
                                    <div className="text-xs text-slate-400 italic">Aucun membre</div>
                                )}
                            </div>

                            {t.note && (
                                <div className="text-[11px] text-slate-500 italic bg-slate-50 rounded px-2 py-1 mb-3">
                                    “{t.note}”
                                </div>
                            )}

                            <button
                                onClick={() => setInviting(t)}
                                disabled={(t.members || []).length === 0 || openMissions.length === 0}
                                className="w-full btn-navy text-xs py-2 disabled:opacity-40 inline-flex items-center justify-center gap-1"
                                title={openMissions.length === 0 ? "Aucune mission ouverte à proposer" : "Inviter cette équipe sur une mission"}
                                data-testid={`team-invite-${t.id}`}
                            >
                                <Send size={12} /> Inviter sur une mission
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <TeamEditModal
                    team={editing.id ? editing : null}
                    favorites={favs}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); refresh(); }}
                />
            )}

            {inviting && (
                <TeamInviteModal
                    team={inviting}
                    openMissions={openMissions}
                    onClose={() => setInviting(null)}
                    onInvited={() => { setInviting(null); refresh(); }}
                />
            )}
        </section>
    );
}

// ----- Edit modal -----
function TeamEditModal({ team, favorites, onClose, onSaved }) {
    const [name, setName] = useState(team?.name || "");
    const [note, setNote] = useState(team?.note || "");
    const [members, setMembers] = useState(new Set(team?.member_user_ids || []));
    const [saving, setSaving] = useState(false);

    const toggle = (uid) => {
        setMembers((cur) => {
            const next = new Set(cur);
            if (next.has(uid)) next.delete(uid); else next.add(uid);
            return next;
        });
    };

    const save = async () => {
        if (!name.trim()) { toast.error("Nom d'équipe requis"); return; }
        setSaving(true);
        try {
            const payload = { name, note, member_user_ids: Array.from(members) };
            if (team?.id) {
                await api.patch(`/client/teams/${team.id}`, payload);
                toast.success("Équipe mise à jour");
            } else {
                await api.post("/client/teams", payload);
                toast.success("Équipe créée");
            }
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-auto py-8 px-4"
            onClick={onClose}
            data-testid="team-edit-modal"
        >
            <div
                className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center px-6 py-5 border-b border-slate-200">
                    <h2 className="font-display text-xl text-[#0B192C]">
                        {team?.id ? "Modifier l'équipe" : "Nouvelle équipe"}
                    </h2>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
                        data-testid="team-edit-close"
                    >
                        <X size={18} />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                            Nom de l'équipe *
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Cocktail Lyon, Serveurs weekend…"
                            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]/40 outline-none"
                            maxLength={80}
                            data-testid="team-name-input"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                            Note interne (facultatif)
                        </label>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={2}
                            maxLength={500}
                            placeholder="Ex. « Équipe habituelle pour les soirées privées de Lyon »"
                            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]/40 outline-none"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                            Membres ({members.size} / {favorites.length} favoris)
                        </label>
                        <div className="mt-2 max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                            {favorites.length === 0 && (
                                <div className="p-4 text-xs text-slate-400 text-center">
                                    Aucun favori — ajoutez d'abord des professionnels à vos favoris.
                                </div>
                            )}
                            {favorites.map((f) => {
                                const isMember = members.has(f.candidate_user_id);
                                return (
                                    <button
                                        key={f.id}
                                        onClick={() => toggle(f.candidate_user_id)}
                                        className={`w-full flex items-center gap-3 px-3 py-2 text-left transition ${
                                            isMember ? "bg-[#F5A623]/10" : "hover:bg-slate-50"
                                        }`}
                                        data-testid={`team-member-toggle-${f.candidate_user_id}`}
                                    >
                                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                            isMember ? "bg-[#F5A623] border-[#F5A623]" : "border-slate-300"
                                        }`}>
                                            {isMember && <Check size={12} className="text-white" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-[#0B192C] truncate">
                                                {f.candidate?.first_name} {f.candidate?.last_name}
                                            </div>
                                            <div className="text-[11px] text-slate-500 truncate">
                                                {categoryLabel(f.candidate?.primary_job)} · {f.candidate?.city || "—"}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
                    <button onClick={onClose} className="btn-ghost-navy text-sm">Annuler</button>
                    <button
                        onClick={save}
                        disabled={saving}
                        className="btn-gold text-sm disabled:opacity-50"
                        data-testid="team-save-btn"
                    >
                        {saving ? "Enregistrement…" : (team?.id ? "Mettre à jour" : "Créer l'équipe")}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ----- Invite-to-mission modal -----
function TeamInviteModal({ team, openMissions, onClose, onInvited }) {
    const [missionId, setMissionId] = useState(openMissions[0]?.id || "");
    const [sending, setSending] = useState(false);

    const send = async () => {
        if (!missionId) return;
        setSending(true);
        try {
            const r = await api.post(`/client/teams/${team.id}/invite-to-mission`, { mission_id: missionId });
            toast.success(`Invitations envoyées à ${r.data.invited} membre(s)`);
            onInvited();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-auto py-8 px-4"
            onClick={onClose}
            data-testid="team-invite-modal"
        >
            <div
                className="bg-white rounded-2xl w-full max-w-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center px-6 py-5 border-b border-slate-200">
                    <h2 className="font-display text-xl text-[#0B192C]">Inviter l'équipe « {team.name} »</h2>
                    <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
                        <X size={18} />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <p className="text-sm text-slate-600">
                        Chaque membre de l'équipe recevra un e-mail personnel avec l'invitation
                        prioritaire à postuler sur cette mission.
                    </p>
                    <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                            Mission ouverte
                        </label>
                        <select
                            value={missionId}
                            onChange={(e) => setMissionId(e.target.value)}
                            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-[#F5A623] outline-none"
                            data-testid="team-invite-mission-select"
                        >
                            {openMissions.map((m) => (
                                <option key={m.id} value={m.id}>
                                    {m.title} — {m.city} · {m.date}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                        <b>{team.members?.length || 0} membre(s)</b> recevront l'invitation prioritaire.
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
                    <button onClick={onClose} className="btn-ghost-navy text-sm">Annuler</button>
                    <button
                        onClick={send}
                        disabled={sending || !missionId}
                        className="btn-gold text-sm disabled:opacity-50 inline-flex items-center gap-1"
                        data-testid="team-invite-confirm"
                    >
                        <UserPlus size={14} /> {sending ? "Envoi…" : "Envoyer les invitations"}
                    </button>
                </div>
            </div>
        </div>
    );
}
